<script setup>
/**
 * SzInAppNotifier — toaster sonner-like pra notificações in-app de novas
 * mensagens em conversas atribuídas ao agente atual.
 *
 * Critérios pra disparar:
 *   - msg.message_type === 0 (incoming)
 *   - conversa.assignee.id === currentUser.id (responsável direto)
 *   - NÃO disparar se conversa ativa NO MOMENTO em que tab está visível
 *     (usuário já tá vendo a mensagem)
 *   - Setting `enable_in_app_notifications` no uiSettings habilitado
 *
 * UX:
 *   - Stack vertical canto inferior direito, máx 3 simultâneos
 *   - Auto-dismiss 5s
 *   - Agrupa mensagens da MESMA conversa (count "X novas mensagens de Y")
 *   - "Responder" abre input inline → envia + dismissa
 *   - Click no card (fora do botão) navega pra conversa
 *   - X dismissa manual
 *
 * Não toca som (projeto já tem audio notifications via AudioNotifications.vue).
 */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';

const store = useStore();
const router = useRouter();
const { uiSettings } = useUISettings();

const MAX_STACK = 3;
const AUTO_DISMISS_MS = 10000;
// SZ_IANN_DEBUG: ligado por padrão durante validação. Pra desligar:
// window.__SZ_IANN_QUIET = true (no console)
function dlog(...args) {
  if (typeof window !== 'undefined' && window.__SZ_IANN_QUIET) return;
  // eslint-disable-next-line no-console
  console.log('[SZ-IANN]', ...args);
}

const notifications = ref([]);
const replyingId = ref(null);
const replyText = ref('');
const replySending = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);
const selectedChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const enabled = computed(() => {
  const v = uiSettings.value?.enable_in_app_notifications;
  // Default: ON. Só desliga se explicitamente false.
  return v === false ? false : true;
});

function isUserViewingConversation(conversationId) {
  if (!selectedChat.value) return false;
  if (selectedChat.value.id !== conversationId) return false;
  // Vê só se tab tá visível. Tab em background → toaster fica visível pra
  // quando voltar (cenário 2.B confirmado pelo operador).
  return document.visibilityState === 'visible';
}

function shouldShow(message) {
  if (!enabled.value) { dlog('skip: setting disabled'); return false; }
  if (!currentUser.value) { dlog('skip: no currentUser'); return false; }
  if (Number(message?.message_type) !== 0) {
    dlog('skip: not incoming', { type: message?.message_type, id: message?.id });
    return false;
  }

  const convFromMsg = message?.conversation;
  const convFromStore = store.getters.getConversationById(message.conversation_id);
  const conv = convFromMsg || convFromStore;
  if (!conv) {
    dlog('skip: conv not found', { convId: message.conversation_id, hadInline: !!convFromMsg });
    return false;
  }

  const assigneeId =
    conv?.meta?.assignee?.id ?? conv?.assignee?.id ?? conv?.assignee_id ?? null;
  if (assigneeId !== currentUser.value.id) {
    dlog('skip: assignee mismatch', {
      assigneeId,
      myId: currentUser.value.id,
      convId: message.conversation_id,
    });
    return false;
  }

  if (isUserViewingConversation(message.conversation_id)) {
    dlog('skip: user viewing this conv', { convId: message.conversation_id });
    return false;
  }

  dlog('SHOW toaster', {
    convId: message.conversation_id,
    msgId: message.id,
    assigneeId,
    myId: currentUser.value.id,
  });
  return true;
}

function attachmentPreview(message) {
  const atts = message?.attachments || [];
  if (!atts.length) return '';
  const t = atts[0]?.file_type || atts[0]?.type || 'arquivo';
  const map = {
    image: '🖼️ Imagem',
    video: '🎥 Vídeo',
    audio: '🎵 Áudio',
    file: '📎 Arquivo',
    location: '📍 Localização',
    contact: '👤 Contato',
  };
  return map[t] || '📎 Anexo';
}

function previewText(message) {
  const content = String(message?.content || '').trim();
  if (content) return content;
  return attachmentPreview(message) || 'Nova mensagem';
}

function senderInfo(message) {
  const conv =
    message?.conversation ||
    store.getters.getConversationById(message.conversation_id);
  const sender =
    message?.sender || conv?.meta?.sender || {};
  return {
    name: sender.name || sender.available_name || 'Contato',
    avatar: sender.thumbnail || sender.avatar_url || '',
  };
}

function senderInitials(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('');
}

function dismiss(id) {
  const idx = notifications.value.findIndex(n => n.id === id);
  if (idx < 0) return;
  clearTimeout(notifications.value[idx].timer);
  notifications.value.splice(idx, 1);
  if (replyingId.value === id) {
    replyingId.value = null;
    replyText.value = '';
  }
}

function resetTimer(notif) {
  clearTimeout(notif.timer);
  notif.timer = setTimeout(() => dismiss(notif.id), AUTO_DISMISS_MS);
}

function addOrUpdate(message) {
  const convId = message.conversation_id;
  const existing = notifications.value.find(n => n.conversationId === convId);
  if (existing) {
    existing.count += 1;
    existing.content = previewText(message);
    existing.lastMessageId = message.id;
    resetTimer(existing);
    return;
  }
  // SZ_SONNER_STACK_20260525: novo no início (nth-child(1)=front), antigo no fim.
  // Limite: descarta o mais antigo (fim do array).
  if (notifications.value.length >= MAX_STACK) {
    const oldest = notifications.value[notifications.value.length - 1];
    clearTimeout(oldest.timer);
    notifications.value.pop();
  }
  const info = senderInfo(message);
  const id = 'sz-iann-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  const notif = {
    id,
    conversationId: convId,
    senderName: info.name,
    avatar: info.avatar,
    avatarError: false, // SZ_AVATAR_FALLBACK: vira true se img falhar
    content: previewText(message),
    count: 1,
    lastMessageId: message.id,
    timer: null,
  };
  notif.timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  notifications.value.unshift(notif);
}

function openConversation(notif) {
  const path =
    '/app/accounts/' + accountId.value + '/conversations/' + notif.conversationId;
  router.push(path).catch(() => { window.location.href = path; });
  dismiss(notif.id);
}

function startReply(notif) {
  replyingId.value = notif.id;
  replyText.value = '';
  // Pausa auto-dismiss enquanto digita
  clearTimeout(notif.timer);
}

function cancelReply(notif) {
  replyingId.value = null;
  replyText.value = '';
  resetTimer(notif);
}

async function sendQuickReply(notif) {
  const txt = replyText.value.trim();
  if (!txt || replySending.value) return;
  replySending.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: notif.conversationId,
      message: txt,
      private: false,
    });
    dismiss(notif.id);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SzInAppNotifier] reply error', e);
  }
  replySending.value = false;
}

// Hook na mutation ADD_MESSAGE (server-side push via ActionCable acaba aqui).
let unsubscribe = null;
onMounted(() => {
  dlog('mounted; currentUser=', currentUser.value?.id, 'enabled=', enabled.value);
  unsubscribe = store.subscribe(mutation => {
    // log de TODAS as mutations relacionadas a mensagem pra debugar (filtra
    // ruído com __SZ_IANN_QUIET=true no console depois)
    if (mutation.type && mutation.type.includes('MESSAGE')) {
      dlog('mutation:', mutation.type, mutation.payload?.id || mutation.payload);
    }
    if (mutation.type !== 'ADD_MESSAGE') return;
    const message = mutation.payload;
    if (!shouldShow(message)) return;
    addOrUpdate(message);
  });
  // Expose helper pra forçar um toaster de teste
  if (typeof window !== 'undefined') {
    window.__szIannTest = function () {
      const fake = {
        message_type: 0,
        conversation_id: store.getters.getSelectedChat?.id || 0,
        id: 'test-' + Date.now(),
        content: 'Mensagem de teste — SZ-IANN',
        conversation: {
          meta: {
            assignee: { id: currentUser.value?.id },
            sender: { name: 'Teste Toaster', thumbnail: '' },
          },
        },
      };
      addOrUpdate(fake);
      dlog('test toaster fired');
    };
  }
});

onUnmounted(() => {
  if (unsubscribe) unsubscribe();
  notifications.value.forEach(n => clearTimeout(n.timer));
  notifications.value = [];
});
</script>

<template>
  <Teleport to="body">
    <div class="sz-iann-stack" :aria-live="'polite'">
      <transition-group name="sz-iann">
        <div
          v-for="n in notifications"
          :key="n.id"
          class="sz-iann-card"
          :class="{ 'sz-iann-card--replying': replyingId === n.id }"
          @click="replyingId !== n.id && openConversation(n)"
        >
          <div class="sz-iann-avatar-wrap">
            <!-- SZ_AVATAR_FALLBACK_20260525: se a URL não carregar (@error),
                 marca n.avatarError=true e renderiza as iniciais. -->
            <img
              v-if="n.avatar && !n.avatarError"
              :src="n.avatar"
              :alt="n.senderName"
              class="sz-iann-avatar"
              @error="n.avatarError = true"
            />
            <div v-else class="sz-iann-avatar sz-iann-avatar--initials">
              {{ senderInitials(n.senderName) }}
            </div>
          </div>

          <div class="sz-iann-body">
            <div class="sz-iann-head">
              <span class="sz-iann-name">{{ n.senderName }}</span>
              <span v-if="n.count > 1" class="sz-iann-count">
                {{ n.count }} novas
              </span>
            </div>

            <div class="sz-iann-content-row">
              <span class="sz-iann-content">{{ n.content }}</span>
              <span class="sz-iann-content-fade" aria-hidden="true" />
              <button
                type="button"
                class="sz-iann-action"
                title="Abrir a conversa"
                @click.stop="openConversation(n)"
              >
                Responder
              </button>
            </div>
          </div>

          <button
            type="button"
            class="sz-iann-close"
            title="Dispensar"
            @click.stop="dismiss(n.id)"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </Teleport>
</template>

<style scoped>
/* SZ_SONNER_STACK_20260525: comportamento sonner — empilhado por padrão,
   expande no hover. Posição: top-center, 440px. z-index 10001 sobrepõe TopBar. */
.sz-iann-stack {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  width: 440px;
  max-width: calc(100vw - 32px);
  height: 88px; /* default: 1 card visível + buffer pros stacked */
  pointer-events: none; /* só os cards são clicáveis */
  font-family: Inter, system-ui, sans-serif;
  transition: height 0.28s ease;
}
/* Hover expande pra mostrar todos os cards alinhados */
.sz-iann-stack:hover { height: 264px; /* 3*64 + 2*16 gap + buffer */ }

/* Cards absolutos, posicionados via nth-child */
.sz-iann-card {
  pointer-events: auto;
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #282828;
  color: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 4px 4px rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  min-height: 64px;
  cursor: pointer;
  transition: top 0.28s ease, transform 0.28s ease, opacity 0.28s ease, box-shadow 0.15s ease;
  transform-origin: top center;
}

/* === Colapsado (default, sem hover) — newest=front, older atrás === */
.sz-iann-stack:not(:hover) > :nth-child(1) {
  top: 16px;
  z-index: 3;
  transform: scale(1);
  opacity: 1;
}
.sz-iann-stack:not(:hover) > :nth-child(2) {
  top: 8px;
  z-index: 2;
  transform: scale(0.96);
  opacity: 0.88;
}
.sz-iann-stack:not(:hover) > :nth-child(3) {
  top: 0;
  z-index: 1;
  transform: scale(0.92);
  opacity: 0.72;
}

/* === Expandido (hover) — todos espaçados verticalmente === */
.sz-iann-stack:hover > .sz-iann-card {
  transform: scale(1);
  opacity: 1;
}
.sz-iann-stack:hover > :nth-child(1) { top: 0; z-index: 3; }
.sz-iann-stack:hover > :nth-child(2) { top: 80px; z-index: 2; }
.sz-iann-stack:hover > :nth-child(3) { top: 160px; z-index: 1; }
/* Modo ESCURO → toaster CLARO */
.dark .sz-iann-card {
  background: #FFFFFF;
  color: #000000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
}
.sz-iann-card--replying { cursor: default; padding-bottom: 14px; }
.sz-iann-card--replying:hover { transform: none; }

.sz-iann-avatar-wrap { flex-shrink: 0; }
.sz-iann-avatar {
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #3D3F43;
  border: 1px solid transparent;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 0; /* hide alt text se img falhar */
}
.dark .sz-iann-avatar {
  background: #E5E5E5;
  border-color: #FFFFFF;
}
.sz-iann-avatar--initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF;
}
.dark .sz-iann-avatar--initials { color: #2B2B2B; }

.sz-iann-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 8px; }

.sz-iann-head {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sz-iann-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #FFFFFF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}
.dark .sz-iann-name { color: #000000; }
.sz-iann-count {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 999px;
  background: #388E3C;
  color: #fff;
  flex-shrink: 0;
}

.sz-iann-content-row {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}
.sz-iann-content {
  flex: 1;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #B2B2B2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  padding-right: 24px;
}
.dark .sz-iann-content { color: #737373; }

/* Fade-out gradient na direita do conteúdo (Figma Rectangle 3095) */
.sz-iann-content-fade {
  position: absolute;
  right: 79px;
  top: 0;
  bottom: 0;
  width: 25px;
  background: linear-gradient(270deg, #282828 0%, rgba(40, 40, 40, 0) 100%);
  pointer-events: none;
}
.dark .sz-iann-content-fade {
  background: linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
}

/* Botão Responder: pill */
.sz-iann-action {
  flex-shrink: 0;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: #5D5E60;
  color: #F3F3F3;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  border: 0;
  cursor: pointer;
  white-space: nowrap;
}
.sz-iann-action:hover { background: #6E6F71; }
.dark .sz-iann-action { background: #DDDDDD; color: #2B2B2B; }
.dark .sz-iann-action:hover { background: #CCCCCC; }

/* X dentro do card, canto superior direito */
.sz-iann-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  padding: 4px;
  border-radius: 999px;
  background: #5D5E60;
  border: 0;
  color: #C1C1C1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}
.sz-iann-close:hover { background: #6E6F71; color: #FAFAFA; }
.dark .sz-iann-close { background: #DDDDDD; color: #2B2B2B; }
.dark .sz-iann-close:hover { background: #CCCCCC; color: #000000; }

/* Quick reply inline */
.sz-iann-reply { display: flex; gap: 6px; align-items: center; margin-top: 6px; width: 100%; }
.sz-iann-input {
  flex: 1;
  background: #1C1E22;
  border: 1px solid #3D3F43;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: #FAFAFA;
  outline: none;
  min-width: 0;
}
.sz-iann-input:focus { border-color: #388E3C; }
.sz-iann-send,
.sz-iann-cancel {
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 0;
  cursor: pointer;
  white-space: nowrap;
}
.sz-iann-send { background: #388E3C; color: #fff; }
.sz-iann-send:disabled { background: #3D3F43; color: #6B7280; cursor: not-allowed; }
.sz-iann-cancel { background: transparent; color: #9CA3AF; }
.sz-iann-cancel:hover { color: #FAFAFA; }

/* SZ_IANN_ANIM_20260526: usar @keyframes porque .sz-iann-stack:not(:hover) > :nth-child(N)
   tem specificity 1,2,1 vs 1,1,0 das classes enter-from/leave-to. Transitions
   ficavam invisíveis (transform inicial sobrescrito imediatamente). Animações
   keyframe rodam por cima das regras CSS estáticas enquanto ativas. */
@keyframes sz-iann-enter-anim {
  0% {
    opacity: 0;
    transform: translateY(-32px) scale(0.94);
  }
  60% { opacity: 1; }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes sz-iann-leave-anim {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-28px) scale(0.9);
  }
}
.sz-iann-enter-active {
  animation: sz-iann-enter-anim 0.34s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}
.sz-iann-leave-active {
  animation: sz-iann-leave-anim 0.24s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  pointer-events: none;
  z-index: 0 !important;
}
</style>
